import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Alert, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import { PictureAsPdf, Refresh, CheckCircle, Warning, Error as ErrorIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ArqueoCaja() {
  const [resumen, setResumen] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cerrando, setCerrando] = useState(false);
  const [efectivoContado, setEfectivoContado] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [resultado, setResultado] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date().toISOString().split('T')[0]);

  // Denominaciones
  const [denoms, setDenoms] = useState({
    '100': 0, '50': 0, '20': 0, '10': 0, '5': 0, '1': 0,
    '0.50': 0, '0.25': 0, '0.10': 0, '0.05': 0, '0.01': 0
  });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargarDatos(); }, [fechaSeleccionada]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/api/arqueo-caja/resumen-turno?fecha=${fechaSeleccionada}`, { headers: headers() }),
        axios.get(`${API_URL}/api/arqueo-caja/historial`, { headers: headers() }),
      ]);
      setResumen(resRes.data?.data);
      setHistorial(histRes.data?.data || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al cargar datos', severity: 'error' });
    }
    setLoading(false);
  };

  // Calcular total desde denominaciones
  const totalDenoms = Object.entries(denoms).reduce((s, [d, c]) => s + (parseFloat(d) * c), 0);

  const handleDenom = (denom, value) => {
    const val = Math.max(0, parseInt(value) || 0);
    setDenoms({ ...denoms, [denom]: val });
    // Auto-actualizar efectivo contado
    const nuevoTotal = Object.entries({ ...denoms, [denom]: val }).reduce((s, [d, c]) => s + (parseFloat(d) * c), 0);
    setEfectivoContado(nuevoTotal.toFixed(2));
  };

  const cerrarCaja = async () => {
    if (!efectivoContado && totalDenoms === 0) {
      return setSnackbar({ open: true, message: 'Ingrese el efectivo contado', severity: 'error' });
    }
    setCerrando(true);
    try {
      const res = await axios.post(`${API_URL}/api/arqueo-caja/cerrar`, {
        efectivoContado: parseFloat(efectivoContado || totalDenoms),
        observaciones,
        denominaciones: totalDenoms > 0 ? denoms : null,
      }, { headers: headers() });
      setResultado(res.data?.data);
      setSnackbar({ open: true, message: 'Caja cerrada exitosamente', severity: 'success' });
      cargarDatos();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error al cerrar caja', severity: 'error' });
    }
    setCerrando(false);
  };

  const verPDF = (arqueoId) => {
    fetch(`${API_URL}/api/arqueo-caja/${arqueoId}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.blob())
      .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
      .catch(() => setSnackbar({ open: true, message: 'Error al generar PDF', severity: 'error' }));
  };

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={700}>Arqueo de Caja</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              type="date"
              size="small"
              value={fechaSeleccionada}
              onChange={(e) => setFechaSeleccionada(e.target.value)}
              sx={{ width: 170 }}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargarDatos}>Actualizar</Button>
          </Box>
        </Box>

        {/* RESUMEN DEL TURNO */}
        {resumen && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Resumen del Turno — {resumen.fecha}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Cajero: {resumen.usuario}</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: '#e8f5e9' }}>
                <Typography variant="caption" color="text.secondary">Total Ventas (Día)</Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">{fmt(resumen.totalVentas)}</Typography>
                <Typography variant="caption">{resumen.cantidadFacturas} facturas + {resumen.cantidadRecibos || 0} recibos</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: '#fff3e0' }}>
                <Typography variant="caption" color="text.secondary">Efectivo Esperado</Typography>
                <Typography variant="h4" fontWeight={700} color="warning.main">{fmt(resumen.efectivoEsperado)}</Typography>
                <Typography variant="caption">En caja</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: '#e3f2fd' }}>
                <Typography variant="caption" color="text.secondary">Total Mes ({resumen.mes})</Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">{fmt(resumen.totalVentasMes)}</Typography>
                <Typography variant="caption">{resumen.cantidadFacturasMes || 0} fact. + {resumen.cantidadRecibosMes || 0} rec.</Typography>
              </Paper>
            </Stack>

            {/* Desglose facturas vs recibos del día */}
            {(resumen.cantidadRecibos > 0 || resumen.cantidadFacturas > 0) && (
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
                  <Typography variant="caption">Facturas SRI</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmt(resumen.totalFacturas)}</Typography>
                  <Typography variant="caption" color="text.secondary">{resumen.cantidadFacturas} docs</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, flex: 1, textAlign: 'center' }}>
                  <Typography variant="caption">Recibos (sin factura)</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmt(resumen.totalRecibos)}</Typography>
                  <Typography variant="caption" color="text.secondary">{resumen.cantidadRecibos || 0} docs</Typography>
                </Paper>
              </Stack>
            )}

            {/* Desglose por forma de pago */}
            {resumen.desglosePorFormaPago?.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Forma de Pago</strong></TableCell>
                      <TableCell align="center"><strong>Facturas</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumen.desglosePorFormaPago.map((fp) => (
                      <TableRow key={fp.codigo}>
                        <TableCell>{fp.nombre}</TableCell>
                        <TableCell align="center">{fp.cantidad}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(fp.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* CIERRE DE CAJA */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Cerrar Caja</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cuente el efectivo en caja y registre el monto. Puede desglosar por denominaciones.
          </Typography>

          {/* Denominaciones */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Billetes y Monedas (opcional)</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {Object.entries(denoms).map(([denom, cant]) => (
              <Box key={denom} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, p: 0.5 }}>
                <Typography variant="caption" sx={{ minWidth: 40, fontWeight: 600 }}>${denom}</Typography>
                <TextField size="small" type="number" value={cant || ''} sx={{ width: 60 }}
                  onChange={(e) => handleDenom(denom, e.target.value)}
                  inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px 8px' } }} />
              </Box>
            ))}
          </Stack>
          {totalDenoms > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>Total por denominaciones: <strong>{fmt(totalDenoms)}</strong></Alert>
          )}

          {/* Efectivo directo */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField label="Efectivo Contado *" type="number" value={efectivoContado}
              onChange={(e) => setEfectivoContado(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }} sx={{ width: 200 }}
              helperText="O use las denominaciones arriba" />
            <TextField label="Observaciones" value={observaciones} fullWidth
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Novedades del turno..." />
          </Stack>

          {/* Diferencia preview */}
          {efectivoContado && resumen && (
            <Alert severity={parseFloat(efectivoContado) === resumen.efectivoEsperado ? 'success' : 'warning'} sx={{ mb: 2 }}>
              Diferencia: <strong>{fmt(parseFloat(efectivoContado) - resumen.efectivoEsperado)}</strong>
              {parseFloat(efectivoContado) === resumen.efectivoEsperado ? ' — Caja cuadrada ✓' :
                parseFloat(efectivoContado) > resumen.efectivoEsperado ? ' — Sobrante' : ' — Faltante'}
            </Alert>
          )}

          <Button variant="contained" size="large" onClick={cerrarCaja} disabled={cerrando}
            sx={{ px: 4, py: 1.5 }}>
            {cerrando ? 'Procesando...' : '🧮 CERRAR CAJA Y GENERAR REPORTE'}
          </Button>
        </Paper>

        {/* RESULTADO DEL CIERRE */}
        {resultado && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: resultado.estado === 'CUADRADO' ? '#e8f5e9' : '#fff3e0' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {resultado.estado === 'CUADRADO' && '✅ Caja Cuadrada'}
                  {resultado.estado === 'SOBRANTE' && '⚠️ Sobrante'}
                  {resultado.estado === 'FALTANTE' && '❌ Faltante'}
                </Typography>
                <Typography variant="body2">
                  Esperado: {fmt(resultado.efectivo_esperado)} | Contado: {fmt(resultado.efectivo_contado)} | Diferencia: {fmt(resultado.diferencia)}
                </Typography>
              </Box>
              <Button variant="outlined" startIcon={<PictureAsPdf />} color="error" onClick={() => verPDF(resultado.id)}>
                Descargar PDF
              </Button>
            </Stack>
          </Paper>
        )}

        <Divider sx={{ my: 3 }} />

        {/* HISTORIAL */}
        <Typography variant="h6" fontWeight={600} gutterBottom>Historial de Arqueos</Typography>
        {historial.length === 0 ? (
          <Alert severity="info">No hay arqueos registrados.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Cajero</strong></TableCell>
                  <TableCell align="right"><strong>Total Ventas</strong></TableCell>
                  <TableCell align="right"><strong>Efectivo Esp.</strong></TableCell>
                  <TableCell align="right"><strong>Contado</strong></TableCell>
                  <TableCell align="right"><strong>Diferencia</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>PDF</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.fecha}</TableCell>
                    <TableCell>{a.usuario_email?.split('@')[0]}</TableCell>
                    <TableCell align="right">{fmt(a.total_ventas)}</TableCell>
                    <TableCell align="right">{fmt(a.efectivo_esperado)}</TableCell>
                    <TableCell align="right">{fmt(a.efectivo_contado)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: a.diferencia === 0 ? 'success.main' : a.diferencia > 0 ? 'warning.main' : 'error.main' }}>
                      {fmt(a.diferencia)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={a.estado} size="small"
                        color={a.estado === 'CUADRADO' ? 'success' : a.estado === 'SOBRANTE' ? 'warning' : 'error'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver PDF">
                        <IconButton size="small" color="error" onClick={() => verPDF(a.id)}>
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
